import { BackHandler, Modal, StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native';
import { Layout, Text, useTheme } from '@ui-kitten/components';
import { Pillar, UIHelper as uh, Units } from '../../../core';

import AssessmentPillars from '../AssessmentPillars';
import AssessmentScore from './AssessmentScore';
import BarChart from './BarChart';
import BiomarkerDetailModal from '../../profileMeMarker/modal/BiomarkerDetailModal';
import { HealthMarkerConversion, NewHealthMarker } from 'src/core/types/HealthMarkerReport';
import { ProfileIcons } from 'src/components/profileMe/ProfileIcons';
import React from 'react';
import { toPreciseNumber } from 'src/core/helpers/NumberHelper';
import { useAnalytics } from 'src/core/hooks/useAnalytics';
import FilledHeartIcon from 'src/features/navigation/components/icons/FilledHeartIcon';
import { getFactor, getUnit } from 'src/core/helpers/UnitHelper';

//props
interface AssessmentBiomarkerProps extends ViewProps {
  biomarker: NewHealthMarker;
  accessibilityLabel?: string;
  testID?: string;
  showDeleteButton?: boolean;
  allMarkerConversions?: HealthMarkerConversion[];
  selectedUnit?: Units;
  onBtnDeleteClick?: () => void;
}

const AssessmentBiomarker = ({
  biomarker,
  accessibilityLabel,
  testID,
  showDeleteButton,
  allMarkerConversions,
  selectedUnit,
  onBtnDeleteClick,
  style
}: AssessmentBiomarkerProps) => {
  const { addAnalyticsLog } = useAnalytics('AssessmentBiomarker.tsx');
  const [modalVisible, setModalVisible] = React.useState(false);
  const hasLoggedMarkerView = React.useRef(false);

  const th = useTheme();

  // styles
  const styleContainer = StyleSheet.create({
    container: {
      marginTop: uh.h2DP(8),
      padding: uh.h2DP(16),
      flexDirection: 'column'
    },
    barChart: {
      marginTop: uh.h2DP(24),
      flex: 1,
      alignItems: 'center'
    },
    icon: {
      width: 36,
      height: 36
    },
    pillarAndScore: {
      flexDirection: 'row',
      marginTop: uh.h2DP(9)
    },
    nameContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    },
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }
  });

  const chatWithKalibraClick = () => {
    setModalVisible(false);
  };

  React.useEffect(() => {
    const backAction = () => {
      if (modalVisible) {
        setModalVisible(false);
        return true;
      }
      return false;
    };

    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);
    return () => backHandler.remove();
  }, [modalVisible]);

  React.useEffect(() => {
    if (modalVisible && !hasLoggedMarkerView.current) {
      addAnalyticsLog({
        data: {
          MarkerId: biomarker.id,
          MarkerDisplayName: biomarker.name
        },
        logType: 'MarkerViewed'
      });
      hasLoggedMarkerView.current = true;
    }
  }, [modalVisible, addAnalyticsLog, biomarker]);

  React.useEffect(() => {
    hasLoggedMarkerView.current = false;
  }, [biomarker.id]);

  const renderModalContentElement = (data: NewHealthMarker) => {
    return (
      <BiomarkerDetailModal
        biomarker={data}
        btnBackHandler={() => setModalVisible(false)}
        btnChatMoreHandler={chatWithKalibraClick}
        btnLearnMoreHandler={chatWithKalibraClick}
        btnDetailAssessmentClick={() => setModalVisible(false)}
        markerConversion={markerConversion}
        selectedUnit={selectedUnit}
      />
    );
  };

  let pillars: Array<Pillar> = [];
  if (biomarker.pillar != null) {
    pillars = [{ name: biomarker.pillar, type: biomarker.pillar.toLowerCase() }];
  }

  const markerConversion = allMarkerConversions?.find((marker) => marker.externalKey == biomarker.externalKey);
  let markerUnit = getUnit(selectedUnit, markerConversion);
  if (markerUnit == undefined) {
    markerUnit = biomarker.unit;
  }

  const factor = getFactor(biomarker.unit, selectedUnit, markerConversion);

  return (
    <Layout
      style={[styleContainer.container, style]}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      testID={testID}>
      <TouchableOpacity onPress={() => setModalVisible(true)}>
        <View style={styleContainer.nameContainer}>
          <Text category="s1">{biomarker.name}</Text>
          <View style={{ flexDirection: 'row', columnGap: uh.w2DP(16), alignItems: 'center' }}>
            {showDeleteButton && (
              <TouchableOpacity onPress={onBtnDeleteClick}>
                <FilledHeartIcon color={th['color-primary-500']} size={25} />
              </TouchableOpacity>
            )}
            <ProfileIcons.ForwardIcon style={styleContainer.icon} fill={th['color-basic-600']} />
          </View>
        </View>
      </TouchableOpacity>
      <View style={styleContainer.pillarAndScore}>
        <AssessmentScore
          caption={'Value'}
          score={`${toPreciseNumber(Number(biomarker.displayValue) * factor)} ${
            biomarker.unit !== 'score' ? markerUnit : `of ${biomarker.maxValue || biomarker.rangeMax?.split('.')[0]}`
          }`}
          textCategory="h5"
        />
        {biomarker.pillar?.length > 0 && (
          <AssessmentPillars caption="Pillar" size="small" showOnePillarOnly={true} pillars={pillars} />
        )}
      </View>
      <BarChart
        currentValue={Number(biomarker.displayValue)}
        healthMarker={biomarker}
        factor={factor}
        style={styleContainer.barChart}
      />

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <View style={[StyleSheet.absoluteFill, styleContainer.backdrop, { justifyContent: 'center' }]}>
          {renderModalContentElement(biomarker)}
        </View>
      </Modal>
    </Layout>
  );
};

export default AssessmentBiomarker;
